import { Button } from "@acme/ui/button";
import { Card } from "@acme/ui/card";
import { Input } from "@acme/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import useGetIsFormReadyToSubmit from "~/common/hooks/useGetIsFormReadyToSubmit.js";
import { api } from "~/common/utils/trpc.js";

export const Route = createFileRoute("/_authed/my-players/$tag/social-media")({
  component: SocialMediaTab,
});

const socialProfilesFormSchema = z.object({
  youtube: z.string().max(100).optional(),
  twitch: z.string().max(100).optional(),
  tiktok: z.string().max(100).optional(),
  instagram: z.string().max(100).optional(),
});

type SocialProfilesForm = z.infer<typeof socialProfilesFormSchema>;

/** Matches backend player.getSocialProfilesByTag output. */
interface SocialProfilesData {
  youtube: string | null;
  twitch: string | null;
  tiktok: string | null;
  instagram: string | null;
}

const PLATFORMS: {
  key: keyof SocialProfilesForm;
  label: string;
  placeholder: string;
  accent: string;
  icon: string;
}[] = [
  {
    key: "youtube",
    label: "YouTube",
    placeholder: "Username or channel",
    accent: "border-l-red-600 bg-red-500/5",
    icon: "▶",
  },
  {
    key: "twitch",
    label: "Twitch",
    placeholder: "Username",
    accent: "border-l-purple-600 bg-purple-500/5",
    icon: "⌗",
  },
  {
    key: "tiktok",
    label: "TikTok",
    placeholder: "@handle",
    accent: "border-l-pink-600 bg-pink-500/5",
    icon: "♪",
  },
  {
    key: "instagram",
    label: "Instagram",
    placeholder: "Username",
    accent: "border-l-amber-600 bg-amber-500/5",
    icon: "◇",
  },
];

function SocialMediaTab() {
  const { t } = useTranslation();
  const { tag } = Route.useParams();
  const queryClient = useQueryClient();

  const { data: profilesData } = useQuery(api.player.getSocialProfilesByTag.queryOptions({ playerTag: tag }));
  const profiles = profilesData;

  const form = useForm<SocialProfilesForm>({
    resolver: zodResolver(socialProfilesFormSchema),
    defaultValues: {
      youtube: "",
      twitch: "",
      tiktok: "",
      instagram: "",
    },
    mode: "onChange",
  });

  const { isFormReadyToSubmit } = useGetIsFormReadyToSubmit({
    control: form.control,
  });

  useEffect(() => {
    if (profiles == null) return;
    form.reset({
      youtube: profiles.youtube ?? "",
      twitch: profiles.twitch ?? "",
      tiktok: profiles.tiktok ?? "",
      instagram: profiles.instagram ?? "",
    });
  }, [profiles, form.reset]);

  const upsert = useMutation(
    api.player.upsertSocialProfiles.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: api.player.getSocialProfilesByTag.queryKey({
            playerTag: tag,
          }),
        });
      },
    })
  );

  const onSubmit = form.handleSubmit((values) => {
    upsert.mutate({
      playerTag: tag,
      youtube: values.youtube || undefined,
      twitch: values.twitch || undefined,
      tiktok: values.tiktok || undefined,
      instagram: values.instagram || undefined,
    });
  });

  const watched = form.watch();

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-1">{t("app.placeholders.socialMediaTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("app.placeholders.socialMediaDescription")}</p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="grid gap-4 sm:grid-cols-2">
            {PLATFORMS.map(({ key, label, placeholder, accent, icon }) => (
              <Controller
                control={form.control}
                key={key}
                name={key}
                render={({ field, fieldState }) => (
                  <div className={`rounded-xl border border-border pl-4 pr-4 py-3 border-l-4 ${accent}`}>
                    <div className="flex items-center gap-3">
                      <span
                        aria-hidden
                        className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-lg font-bold text-muted-foreground">
                        {icon}
                      </span>
                      <div className="min-w-0 flex-1 space-y-1">
                        <label className="text-xs font-medium text-muted-foreground" htmlFor={field.name}>
                          {label}
                        </label>
                        <Input
                          {...field}
                          aria-invalid={fieldState.invalid}
                          className="h-9 bg-background/80"
                          id={field.name}
                          placeholder={placeholder}
                        />
                        {fieldState.error?.message && (
                          <p className="text-xs text-destructive">{fieldState.error.message}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              />
            ))}
          </div>

          {Object.values(watched).some(Boolean) && (
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">{t("app.placeholders.socialPreview")}</p>
              <ul className="flex flex-wrap gap-2">
                {PLATFORMS.filter((p) => watched[p.key]).map(({ key, label, icon }) => (
                  <li className="inline-flex items-center gap-1.5 rounded-md bg-background px-2 py-1 text-sm" key={key}>
                    <span className="text-muted-foreground">{icon}</span>
                    <span>{watched[key] || label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button disabled={!isFormReadyToSubmit || upsert.isPending} type="submit">
            {t("app.placeholders.socialUpdate")}
          </Button>
        </form>
      </div>
    </Card>
  );
}
